/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useEffect, useRef } from 'react';

/**
 * Wires a Marketplace tab to the free-text search term owned by MarketplacePage.
 *
 * Returns a ref holding the current term. Tabs read it inside their fetch function
 * instead of taking it as an argument, so the term survives calls made by the shared
 * `handleSort` / `handleFilter` helpers and by pagination — none of which know about it.
 *
 * `onSearchChange` fires only when the term actually changes, never on mount, so it
 * does not duplicate the tab's own initial fetch.
 */
const useGlobalSearch = (searchText: string, onSearchChange: (searchedText: string) => void) => {
  const searchTextRef = useRef(searchText);
  const previousSearchTextRef = useRef(searchText);

  searchTextRef.current = searchText;

  useEffect(() => {
    if (previousSearchTextRef.current === searchText) {
      return;
    }
    previousSearchTextRef.current = searchText;
    onSearchChange(searchText);
    // onSearchChange is intentionally excluded: it is re-created every render and is
    // only ever meant to run in response to the term itself changing.
  }, [searchText]);

  return searchTextRef;
};

export default useGlobalSearch;
